/**
 * Second step: the error the merchant clicked Resolve on, then the groups of shipment settings
 * they may need to fix. The screen owns what surrounds the card list - including the states of
 * the prefetch that fills it - and the list owns itself.
 */
import { observer } from "mobx-react";
import FontBody1 from "../../../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR } from "../../../../../../../components/fonts/font-props";
import { Button } from "../../../../../../../components/inputs/button/button";
import ListLayout, { LIST_LAYOUT_SPACING_VARIANT } from "../../../../../../../components/layouts/list-layout/list-layout";
import RoundLoader, { RoundLoaderType } from "../../../../../../../components/loaders/round-loader/round-loader";
import { Note, NOTE_TYPE } from "../../../../../../../components/note/note";
import useValidationGroup from "../../../../../../../hooks/validation-group/validation-group-hook";
import { IResolveLabelErrorScreenProps } from "../../../../stores/rma-action-store/resolve-label-error-action-store";
import ValidateShippingDetailsCards from "./validate-shipping-details-cards";
import "./validate-shipping-details-screen.scss";

const ValidateShippingDetailsScreen: React.FC<IResolveLabelErrorScreenProps> = observer(({ store }) => {
    const validateStore = store.validateShippingDetails;
    /**
     * One group for the whole screen: the cards register their fields into it, and the action
     * below them reads whether what the merchant typed is good enough to continue.
     */
    const validationGroup = useValidationGroup();

    return (
        <div className="validate-shipping-details-screen">
            <ListLayout>
                <ListLayout spacingVariant={LIST_LAYOUT_SPACING_VARIANT.SMAll}>
                    <FontBody1 color={FONT_COLOR.NEUTRAL3}>
                        We have encountered an error while generating your label:
                    </FontBody1>

                    {store.errorNote && (
                        <Note caption={store.errorNote.caption} body={store.errorNote.body} />
                    )}
                </ListLayout>

                {validateStore.isLoading && (
                    <div className="validate-shipping-details-screen-loader">
                        <RoundLoader type={RoundLoaderType.Dark} />
                    </div>
                )}

                {validateStore.loadFailed && (
                    <Note
                        type={NOTE_TYPE.ERROR}
                        caption="Could not load the shipment details"
                        body="Close this window and open it again to retry."
                    />
                )}

                {/** One condition rather than three states: load() empties it on the way in and fills
                  * it only on success, so this is exactly "there is something to show". */}
                {validateStore.shippingDetails && (
                    <>
                        <ValidateShippingDetailsCards store={validateStore} validationGroup={validationGroup} />

                        <div className="resolve-label-error-modal-actions">
                            {/**
                              * Off until the cards are filled in: a required field says so the
                              * moment the card opens, so the button has nothing left to explain.
                              * A method no carrier quotes has no rate screen to continue to, so the
                              * details are approved from here.
                              */}
                            <Button
                                text={validateStore.isQuotableMethod ? "Continue" : "Approve RMA"}
                                hasLoader
                                isLoading={validateStore.isQuoting || validateStore.isApproving}
                                disabled={!validationGroup.isValid()}
                                onClick={() => { void store.continueFromShippingDetails(); }}
                            />
                        </div>
                    </>
                )}
            </ListLayout>
        </div>
    );
});

export default ValidateShippingDetailsScreen;
